// src/components/DataPanel.js
import React from "react";
import { useDispatch, connect } from "react-redux";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LayerDataSelection from "./LayerDataSelection";
import LayerSelection from "./LayerSelection";
import {
  toggleLayerState,
  updateBms,
  updateSld,
  setActiveField,
  setSecondLayerOpacity,
} from "../actions";
import { generateSld } from "../utils/style-generator";
import {
  ColorUtils,
  calculateFieldStats,
  calculatePercentiles,
} from "../utils/helpers";
import { Button, Paper } from "@mui/material";
import Slider from "@mui/material/Slider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import preloaded from "../utils/data.json";

const mapStateToProps = (state) => ({ map: state.MapReducer });

const SRTM_LAYER_ID = "allum_project:SRTM90_4326"; // ✅ új DEM réteg (default layers között)

function getFieldsForLayer(layers, qualifiedLayerName) {
  const fs = layers?.[qualifiedLayerName]?.fieldStyles || {};
  return Object.values(fs).flat();
}

// biztosítjuk, hogy a DEM mindig legalul legyen (WMS LAYERS sorrendben legelső)
function ensureSrtmFirst(layerIds) {
  const arr = Array.isArray(layerIds) ? [...layerIds] : [];
  const has = arr.includes(SRTM_LAYER_ID);
  const rest = arr.filter((x) => x !== SRTM_LAYER_ID);
  return has ? [SRTM_LAYER_ID, ...rest] : rest;
}

// ---- Croatian historical admin basemap switching ----

const HR_1785_ADMIN_BMAPS = [
  "allum_project:hr_jaras_1785",
  "allum_project:hr_1785",
  "allum_project:hr_megye_1785",
];

const HR_1828_ADMIN_BMAPS = [
  "allum_project:hr_jaras_1785",
  "allum_project:hr_1785",
  "allum_project:hr_megye_1785",
];

const HR_1910_ADMIN_BMAPS = [
  "allum_project:hr_jaras",
  "allum_project:hr1912",
  "allum_project:hr_megye",
];

const HR_ALL_ADMIN_BMAPS = [
  ...HR_1785_ADMIN_BMAPS,
  ...HR_1828_ADMIN_BMAPS,
  ...HR_1910_ADMIN_BMAPS,
];

function isHr1785Layer(layerId) {
  return layerId === "allum_project:hr_settlements_1785";
}

function isHr1828Layer(layerId) {
  return layerId === "allum_project:hr_settlements_1828";
}

function isHr1910Layer(layerId) {
  return layerId === "allum_project:hr_settlements_1910";
}

function normalizeHrAdminBasemaps(activeLayer, enabledBmaps) {
  const current = Array.isArray(enabledBmaps) ? enabledBmaps : [];

  // Csak a horvát settlement főrétegeknél cserélünk admin boundary-t.
  // Más rétegeknél nem nyúlunk hozzá a listához.
  if (
    !isHr1785Layer(activeLayer) &&
    !isHr1828Layer(activeLayer) &&
    !isHr1910Layer(activeLayer)
  ) {
    return ensureSrtmFirst(current);
  }

  let targetAdminBmaps = HR_1910_ADMIN_BMAPS;

  if (isHr1785Layer(activeLayer)) {
    targetAdminBmaps = HR_1785_ADMIN_BMAPS;
  }

  if (isHr1828Layer(activeLayer)) {
    targetAdminBmaps = HR_1828_ADMIN_BMAPS;
  }

  const filtered = current.filter((id) => !HR_ALL_ADMIN_BMAPS.includes(id));

  return ensureSrtmFirst([
    ...filtered,
    ...targetAdminBmaps,
  ]);
}

const DataPanel = (props) => {
  const dispatch = useDispatch();

  const [showPanel, setShowPanel] = React.useState(false);
  const [showBMaps, setShowBMaps] = React.useState(false);

  const [opacity, setOpacity] = React.useState(100);
  const [opacityBASE, setOpacityBASE] = React.useState(90);

  const secondOpacityFromStore = props.map.secondLayer?.opacity ?? 1;
  const [secondOpacity, setSecondOpacity] = React.useState(
    Math.round(secondOpacityFromStore * 100)
  );

  // passzív figyelő, hogy a dualLayerChanged eseményre újrarendereljen a panel
  const [, forceDualTick] = React.useState(0);
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => forceDualTick((t) => t + 1);
    window.addEventListener("dualLayerChanged", handler);
    return () => window.removeEventListener("dualLayerChanged", handler);
  }, []);

  const activeField = props.map.activeField;
  const [colorRamp, setColorRamp] = React.useState(props.map.colorRamp);
  const [division, setDivision] = React.useState(9);
  const [styleType, setStyleType] = React.useState("categorical");
  const [styleTypes, setStyleTypes] = React.useState(["range", "categorical"]);
  const [stats, setStats] = React.useState({
    min: 0,
    max: 0,
    percentiles: [],
    values: [],
  });

  const [layerStyle, setLayerStyle] = React.useState({
    colorRamp: colorRamp,
    breaks: [],
    opacity: 1,
  });

  const fieldsForActiveLayer = React.useMemo(() => {
    return getFieldsForLayer(props.map?.layers || {}, props.map?.activeLayer);
  }, [props.map.layers, props.map.activeLayer]);

  React.useEffect(() => {
    const fromStore = props.map.secondLayer?.opacity;
    if (typeof fromStore === "number") {
      setSecondOpacity(Math.round(fromStore * 100));
    }
  }, [props.map.secondLayer?.opacity]);

  React.useEffect(() => {
    if (!props.map.activeLayer) return;
    if (!activeField || !fieldsForActiveLayer.includes(activeField)) {
      const first = fieldsForActiveLayer[0] || null;
      if (first) dispatch(setActiveField(first));
    }
  }, [props.map.activeLayer, fieldsForActiveLayer, activeField, dispatch]);

  const loadFieldStats = (propsArg) => {
    if (!propsArg.map?.activeLayer || !activeField) return;
    if (!fieldsForActiveLayer.includes(activeField)) return;

    calculateFieldStats(
      "http://193.225.239.5/geoserver/allum_project/ows",
      propsArg.map.activeLayer,
      activeField
    )
      .then((_stats) => setStats(_stats))
      .catch(() => {});
  };

  const reloadSld = () => {
    if (!props.map?.activeLayer || !activeField) return;
    if (!fieldsForActiveLayer.includes(activeField)) return;

    const sld = generateSld(
      {
        qualifiedName: props.map.activeLayer,
        name: props.map.activeLayer.split(":")[1],
        fieldName: activeField,
        style: layerStyle,
      },
      styleType
    );

    fetch("http://193.225.239.5/backend2/saveSld", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ sldData: sld }),
    })
      .then((r) => r.json())
      .then((r) => {
        if (!r.error && r.data?.id) {
          dispatch(updateSld(props.map.activeLayer, r.data.id, layerStyle));
        } else {
          console.log("Error saving sld", r);
        }
      })
      .catch(() => {});
  };

  React.useEffect(() => {
    if (!props.map?.activeLayer || !activeField) return;
    if (!fieldsForActiveLayer.includes(activeField)) return;

    const fs = props.map.layers[props.map.activeLayer]?.fieldStyles || {};
    const supportedFieldTypes = Object.keys(fs)
      .map((k) => (fs[k]?.includes(activeField) ? k : false))
      .filter(Boolean);

    if (supportedFieldTypes.indexOf(styleType) === -1) {
      setStyleType(supportedFieldTypes[0]);
      setStyleTypes(supportedFieldTypes);
      setStats({ min: 0, max: 0, percentiles: [], values: [] });
    }
    loadFieldStats(props);
  }, [props.map.activeLayer, activeField]);

  React.useEffect(() => {
    reloadSld();
  }, [layerStyle]);

  React.useEffect(() => {
    const next = normalizeHrAdminBasemaps(
      props.map.activeLayer,
      props.map.enabledBmaps
    );
  
    const prevKey = ensureSrtmFirst(props.map.enabledBmaps || []).join("|");
    const nextKey = next.join("|");
  
    if (prevKey !== nextKey) {
      dispatch(updateBms(next));
    }
  }, [props.map.activeLayer, props.map.enabledBmaps, dispatch]);

  // ✅ Itt javítjuk a MAIN GRID színezést: ugyanaz, mint a second layer gridnél
  React.useEffect(() => {
    const fieldColorRampMap = {
      item_count: "colramp05",
      type_count: "colramp12",
      style_count: "colramp07",
      informant_count: "colramp13",
      collector_count: "colramp09",

      tancfolyamat_count: "colramp09",
      kezirat_count: "colramp07",
      kezirat_page_count: "WhiteBlack",
      foto_osszes_count: "colramp08",
      foto_ff_count: "WhiteBlack",
      foto_szines_count: "colramp13",
      tanckataszter_count: "colramp07",

    };
    
    const forcedRampName = fieldColorRampMap[activeField] || colorRamp;
    
    const forcedRampColors =
      (props.map.colorRamps?.[forcedRampName]?.colors) ||
      (props.map.colorRamps?.colrampgrid?.colors) ||
      ["#000000"];
    
    const isCountField =
      typeof activeField === "string" &&
      (activeField.endsWith("_count") || activeField.includes("count"));

    const values = stats.values || [];
    const activeLayerId = props.map.activeLayer || "";

    const isGridOrDiff =
      activeLayerId.includes("grid") || activeLayerId.includes("kulonb");
    
    if (!values.length && !activeField) return;

    const meta = activeField && preloaded.find((o) => o.name === activeField);

    let rawBreaks = [];
    let color_preloaded = [];
    let breaks_preloaded = [];

    if (styleType === "range") {
      if (!values.length) return;

      const isCountField =
      typeof activeField === "string" &&
      (activeField.endsWith("_count") || activeField.includes("count"));
    
      const steps = (() => {
        // 0 + 6 osztály = 7 break (általában nagyon jól olvasható)
        // (ha ragaszkodsz 8-hoz, lentebb írom)
        if (division === "quantile") return [0, 1/6, 2/6, 3/6, 4/6, 5/6, 1];
      
        if (division === "quintile") return [0, 0.2, 0.4, 0.6, 0.8, 1];
      
        // default: 10 osztály (11 break)
        return [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
      })();
      
      const hasZero = values.some(v => Number(v) === 0);
      rawBreaks = calculatePercentiles(
        steps,
        values,
        isCountField
          ? { splitZero: hasZero, integerBreaks: true }
          : { splitZero: false, integerBreaks: false }
      );

      // RANGE: a data.json-ből jönnek a törések és a színek
      if (meta && Array.isArray(meta.breaks) && Array.isArray(meta.colors)) {
        breaks_preloaded = meta.breaks;
        color_preloaded = meta.colors;
        rawBreaks = breaks_preloaded;
      }
    } else {
      // CATEGORICAL
      if (isGridOrDiff) {
        // MAIN GRID: unique numeric values + colrampgrid + ColorUtils.getColor (second logika)
        const uniq = [...new Set(values.map((v) => Number(v)))]
          .filter((v) => Number.isFinite(v))
          .sort((a, b) => a - b);
        rawBreaks = uniq;
      } else {
        // MAIN categorical (nem grid): data.json meta.breaks/meta.colors
        if (meta && Array.isArray(meta.breaks) && meta.breaks.length) {
          rawBreaks = meta.breaks.slice();
          color_preloaded = Array.isArray(meta.colors) ? meta.colors.slice() : [];
        } else if (values.length) {
          rawBreaks = [...new Set(values)].filter(
            (v) => v !== null && v !== undefined
          );
        }
      }
    }

    if (!rawBreaks.length) return;

    setLayerStyle((prev) => {
      // MAIN GRID-hez mindig colrampgrid-et használunk (mint a second)
      const baseGridRamp =
        (props.map.colorRamps &&
          props.map.colorRamps.colrampgrid &&
          props.map.colorRamps.colrampgrid.colors) ||
        [];

      const fallbackRamp = forcedRampColors;

      const breaks = rawBreaks
        .map((b, idx) => {
          if (styleType === "range") {
            const next =
              idx + 1 < rawBreaks.length ? rawBreaks[idx + 1] : null;
            if (next == null && idx !== 0) return null;

            const from = Number(b);
            const toRaw = next != null ? Number(next) : null;
            if (!Number.isFinite(from)) return null;
            
            const isInt = isCountField;
            
            let rangeFrom = from;
            let rangeTo = toRaw; // default: nem-count mezők
            
            if (isInt) {
              // COUNT mezőknél a bin felső határát inclusive-ra alakítjuk:
              // [from, next)  ->  [from, next-1]
              if (toRaw != null && Number.isFinite(toRaw)) {
                rangeTo = toRaw - 1;
              } else {
                rangeTo = null; // utolsó: ≥ from
              }
            }
            
            const hasZeroCategory =
              rawBreaks.length > 2 &&
              Number(rawBreaks[0]) < 0 &&
              Number(rawBreaks[1]) === 0;

            const label = (() => {
              // 1785 horvát: a [-999999, 0] első intervallum legyen külön 0 kategória
              if (hasZeroCategory && idx === 0) {
                return "= 0 / n.a.";
              }

              // 1785 horvát: a 0 utáni első pozitív tartomány címkéje ne legyen félreérthető
              if (hasZeroCategory && idx === 1) {
                return `> 0 - ${toRaw}`;
              }

              if (!isInt) {
                return idx === 0
                  ? `< ${toRaw}`
                  : idx === rawBreaks.length - 2
                  ? `> ${from}`
                  : `${from} - ${toRaw}`;
              }

              if (idx === 0 && from === 0) return `0`;
              if (rangeTo == null) return `≥ ${from}`;

              if (from === rangeTo) return `${from}`;
              return `${from} – ${rangeTo}`;
            })();
            
            const c = color_preloaded.length
              ? color_preloaded[idx % color_preloaded.length]
              : ColorUtils.getColor(
                  idx,
                  rawBreaks.length,
                  fallbackRamp.length ? fallbackRamp : ["#000000"]
                );
            
            // FONTOS: values most már COUNT esetén inclusive [from, toInclusive]
            return { name: label, values: [rangeFrom, rangeTo], color: c, type: "range" };
          }

          // CATEGORICAL
          let label = String(b);

          if (activeField === "c_t007" && Number(b) === 0) {
            label = "0 / n.a";
          }

          if (!label || label === "NaN") return null;

          if (isGridOrDiff) {
            // main grid szín: ugyanúgy számoljuk, mint a second gridnél
            const c = ColorUtils.getColor(
              idx,
              rawBreaks.length,
              baseGridRamp.length ? baseGridRamp : ["#000000"]
            );
            return { name: label, values: [b, b], color: c, type: "categorical" };
          }

          // non-grid categorical: data.json colors (vagy fallback ramp)
          const c = color_preloaded.length
            ? color_preloaded[idx % color_preloaded.length]
            : ColorUtils.getColor(
                idx,
                rawBreaks.length,
                fallbackRamp.length ? fallbackRamp : ["#000000"]
              );

          return { name: label, values: [b, b], color: c, type: "categorical" };
        })
        .filter(Boolean);

      if (!breaks.length) return prev;

      const forcedRamp = fieldColorRampMap[activeField] || colorRamp;

      return {
        ...prev,
        colorRamp: forcedRampName,   // <- ez legyen forced
        division,
        breaks,
        styleType,
        opacity: opacity / 100,
      };
    });
  }, [
    stats,
    styleType,
    division,
    colorRamp,
    opacity,
    activeField,
    props.map.activeLayer,
    props.map.colorRamps,
  ]);

  React.useEffect(() => {
    setLayerStyle((ls) => ({
      colorRamp: ls.colorRamp,
      breaks: ls.breaks,
      opacity: opacity / 100,
    }));
  }, [opacity]);

  React.useEffect(() => {
    if (props.map.layers["allum_project:basemap"]) {
      props.map.layers["allum_project:basemap"].opacity = opacityBASE / 100;
      reloadSld();
    }
  }, [opacityBASE]);

  // ✅ Default layers lista:
  // Horvát 1785-nél az 1785-ös admin boundary-k,
  // horvát 1910-nél az 1910-es admin boundary-k jelennek meg.
  // Magyar maradványok: megye1785, settlements_1828 logika törölve.

  const COMMON_BMAPS = [
    SRTM_LAYER_ID,
    "allum_project:allut_lines",
    "allum_project:megyeiut_lines",
    "allum_project:kozlut_lines",
    "allum_project:postautak1808",
    "allum_project:vasutallomas",
    "allum_project:vasutvonal",
    "allum_project:kikoto",
    "allum_project:folyokl",
    "allum_project:folyokm",
    "allum_project:tavak",
    "allum_project:country_borders_2016",
  ];

  const activeHrAdminBmaps = (() => {
    if (isHr1785Layer(props.map.activeLayer)) return HR_1785_ADMIN_BMAPS;
    if (isHr1828Layer(props.map.activeLayer)) return HR_1828_ADMIN_BMAPS;
    return HR_1910_ADMIN_BMAPS;
  })();
  
  let allIds = [
    ...COMMON_BMAPS,
    ...activeHrAdminBmaps,
  ];

  allIds = ensureSrtmFirst(allIds);

  const basemapChange = (flag, bmId) => {
    const fullId = bmId.startsWith("allum_project:") ? bmId : "allum_project:" + bmId;
  
    const prevBmIds = ensureSrtmFirst([...props.map.enabledBmaps]);
    const cBmids = [];
  
    allIds.forEach((e) => {
      if (!flag && fullId === e) {
        // remove
      } else {
        if (prevBmIds.indexOf(e) > -1 || (flag && fullId === e)) cBmids.push(e);
      }
    });
  
    dispatch(updateBms(ensureSrtmFirst(cBmids)));
  };

  // kis cím override, ha nincs benne data.json-ben
  const titleOverride = (layerShort) => {
    if (layerShort === "SRTM90_4326") return "Elevation model (SRTM 90m)";
    return null;
  };

  return (
    <div>
      {!showPanel ? (
        <Tooltip title="Open layer and data panel">
          <IconButton
            style={{ color: "gray" }}
            onClick={() => setShowPanel(true)}
            aria-label="open"
          >
            <MenuIcon />
          </IconButton>
        </Tooltip>
      ) : (
        ""
      )}

      <Paper
        elevation={10}
        style={{
          display: showPanel ? "block" : "none",
          padding: 16,
          minWidth: 150,
          maxWidth: "37vmax",
          paddingLeft: 32,
          paddingRight: 32,
          maxHeight: "calc(100vh - 32px)",
          overflowY: "auto",
          borderRadius: 10,
          backgroundColor: "#f0f0f0",
        }}
      >
        <IconButton
          style={{ color: "gray", float: "right" }}
          onClick={() => setShowPanel(false)}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>

        <br />
        <br />

        <Button
          size="small"
          variant="contained"
          onClick={() => setShowBMaps(!showBMaps)}
          endIcon={!showBMaps ? "" : <CloseIcon />}
        >
          {!showBMaps ? "Toggle default layers" : "Close"}
        </Button>

        <Paper
          elevation={10}
          style={{
            display: showBMaps ? "block" : "none",
            border: 1,
            borderStyle: "solid",
            padding: 15,
            borderColor: "#e0e0e0",
            marginTop: 10,
            borderRadius: 15,
            backgroundColor: "#ffffff",
          }}
        >
          {allIds.map((bmId) => {
            const short = bmId.replace("allum_project:", "");
            const found = preloaded.find((obj) => obj.name === short);

            const isSrtm = short === "SRTM90_4326";

            return (
              <div key={short}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={props.map.enabledBmaps.indexOf(bmId) > -1}
                    />
                  }
                  onClick={(e) => basemapChange(e.target.checked, bmId)}
                  label={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* LEGEND + TITLE WRAPPER (SRTM-nél speciális) */}
                      <div
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "flex-start",
                        }}
                      >
                        {/* LEGEND IMAGE */}
                        <img
                          alt={short}
                          src={
                            isSrtm
                              ? require("../images/srtm_legend.png")
                              : "http://193.225.239.5/geoserver/wms?REQUEST=GetLegendGraphic" +
                                "&VERSION=1.0.0&FORMAT=image/png&WIDTH=20&HEIGHT=20&LAYER=allum_project:" +
                                short
                          }
                          style={
                            isSrtm
                              ? {
                                  width: 67,
                                  height: 231,
                                  objectFit: "contain",
                                }
                              : {
                                  width: 20,
                                  height: 20,
                                  marginTop: 9,
                                }
                          }
                        />

                        {/* TITLE */}
                        <div
                          style={
                            isSrtm
                              ? {
                                  position: "absolute",
                                  left: 80,              // kép mellé
                                  top: "50%",            // vertikális közép
                                  transform: "translateY(-50%)",
                                  whiteSpace: "nowrap",
                                }
                              : {
                                  position: "relative",
                                  left: 15,
                                  top: 6,
                                  width: "500%",
                                }
                          }
                        >
                          {titleOverride(short) || found?.title || short}
                        </div>
                      </div>
                    </div>
                  }
                />
                <br />
              </div>
            );
          })}


        </Paper>

        <LayerDataSelection
          onLayerChange={(v) => {
            const url = props.map.layers?.[v]?.url || null;
            if (
              typeof window !== "undefined" &&
              window.__dualLayer?.enabled &&
              window.__dualLayerTarget === 2
            ) {
              window.__dualLayer = {
                ...(window.__dualLayer || {}),
                baseLayerId: v,
                baseLayerUrl: url,
              };
              window.dispatchEvent(
                new CustomEvent("dualLayerChanged", {
                  detail: {
                    reason: "topicChangeSecond",
                    baseLayerId: v,
                    baseLayerUrl: url,
                  },
                })
              );
            } else {
              dispatch(toggleLayerState(v));
              const fields = getFieldsForLayer(props.map?.layers || {}, v);
              if (!fields.includes(activeField)) {
                const first = fields[0] || null;
                if (first) dispatch(setActiveField(first));
              }
            }
          }}
        />

        <br />
        <Typography variant="caption" gutterBottom component="div">
          Default layer opacity:
        </Typography>
        <Slider
          aria-label="Default layer opacity"
          value={opacityBASE}
          onChange={(e) => setOpacityBASE(e.target.value)}
        />

        <Typography variant="caption" gutterBottom component="div">
          Main layer opacity:
        </Typography>
        <Slider
          aria-label="Main layer opacity"
          value={opacity}
          onChange={(e) => setOpacity(e.target.value)}
        />

        {(props.map.secondLayer?.enabled ||
          (typeof window !== "undefined" && window.__dualLayer?.enabled)) && (
          <>
            <Typography variant="caption" gutterBottom component="div">
              Second layer opacity:
            </Typography>
            <Slider
              aria-label="Second layer opacity"
              value={secondOpacity}
              onChange={(e, val) => {
                const v = Array.isArray(val) ? val[0] : val;
                setSecondOpacity(typeof v === "number" ? v : 70);
              }}
              onChangeCommitted={(e, val) => {
                const v = Array.isArray(val) ? val[0] : val;
                const op = Math.max(5, Math.min(100, v)) / 100;
                (props.dispatch || dispatch)(setSecondLayerOpacity(op));
                if (typeof window !== "undefined") {
                  window.__dualLayer = window.__dualLayer || {};
                  window.__dualLayer.opacity = op;
                  window.dispatchEvent(
                    new CustomEvent("dualLayerChanged", {
                      detail: { reason: "opacityFromSlider", opacity: op },
                    })
                  );
                }
              }}
              min={5}
              max={100}
              step={5}
            />
          </>
        )}

        <LayerSelection />
      </Paper>
    </div>
  );
};

export default connect(mapStateToProps)(DataPanel);
